<template>
  <div class="canary-dashboard" redesign>
    <!-- Header -->
    <div class="local-hero local-hero-dark staff">
      <h1 class="local-hero-header" v-translate>Canary Dashboard</h1>
    </div>

    <div class="forms header-container">
      <div class="Report__Filter">
        <md-field>
          <label for="selectedDateRange">Date filter</label>
          <md-select
            @input="dateRangeChanged"
            :value="selectedDateRange"
            class="box"
          >
            <md-option value="all_time">All time</md-option>
            <md-option value="year_to_date">Year to Date</md-option>
            <md-option value="custom">Custom Date Range</md-option>
          </md-select>
        </md-field>

        <div
          v-if="isCustomDatePickerVisible"
          class="Report__Filter__DateRange"
        >
          <md-field
            class="md-override Report__Filter__DateRange-field"
            md-inline
          >
            <md-datepicker
              class="md-override Report__Filter__DateRange-picker"
              v-model="selectedStartDate"
              md-immediately
            >
              <label>Start date</label>
            </md-datepicker>
          </md-field>

          <md-field
            class="md-override Report__Filter__DateRange-field"
            md-inline
          >
            <md-datepicker
              class="md-override Report__Filter__DateRange-picker"
              v-model="selectedEndDate"
              md-immediately
            >
              <label>End date</label>
            </md-datepicker>
          </md-field>
        </div>
      </div>
    </div>

    <!-- Filterable metrics -->
    <h2 class="canary-dashboard-heading">Metrics</h2>
    <template v-if="loading">
      <LoadSpinner />
    </template>
    <div v-else class="canary-dashboard-container form-container">
      <!-- Show all filtered metrics -->
      <div class="metric-container form-field" v-for="metricName in enabledMetrics">
        <div class="metric-header">
          <!-- Metric name (friendly) -->
          <label class="metric-label">
            {{ friendlyMetricNames[metricName] }}
          </label>

          <!-- Metric details tooltip -->
          <div class="metric-tooltip-wrapper" v-if="metricDetails[metricName] !== ''">
            <img src="@/assets/icons/information-icon.png" />
            <div class="metric-tooltip card-shape">
              {{ metricDetails[metricName] }}
            </div>
          </div>
        </div>

        <!-- Metric data -->
        <div
          class="metric-data card-shape"
          :class="metrics[metricName].data[metricName] === 'N/A' ? '' : metricName"
          v-html="metrics[metricName].data[metricName]"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import { analyticsMixin } from '@/services/googleAnalytics'
import CanaryDashboard from '@/services/canaryDashboard'
import LoadSpinner from '@/components/LoadSpinner'

const FILTERABLE_METRICS = [
  'requests_avg_completion_time',
  'dollars_granted',
  'grant_conversion_rate',
  'request_approval_rate',
  'request_abandon_rate',
  'sms_notifications_rate',
  'gm_messages_sent',
]

const ALL_TIME_METRICS = [
  'dollars_contributed',
  'request_drop_off',
  'pinwheel_integration_rate',
]

const FRIENDLY_METRIC_NAMES = {
  requests_avg_completion_time: 'Average request completion time',
  dollars_granted: 'Dollars granted',
  dollars_contributed: 'Dollars contributed',
  grant_conversion_rate: 'Grant conversion rate',
  pinwheel_integration_rate: 'HR / payroll Integration',
  request_drop_off: 'Request drop off',
  sms_notifications_rate: 'Employee SMS notifications rate',
  gm_messages_sent: 'GM messages sent',
  request_approval_rate: 'Grant approval rate',
  request_abandon_rate: 'Grant abandon rate',
}

const METRIC_DETAILS = {
  requests_avg_completion_time: 'Time to complete requests. (HH:MM:SS)',
  dollars_granted: 'Total of all payouts. (USD $)',
  dollars_contributed: 'Total of all external donations. (USD $, all time)',
  grant_conversion_rate: 'Percentage of approved or partially approved requests out of the total started.',
  pinwheel_integration_rate: 'Percentage of clients using Pinwheel HR integration. (all time)',
  request_drop_off: 'Request flow drop off counts. (all time)',
  sms_notifications_rate: 'Percentage of employees with SMS notifications enabled.',
  gm_messages_sent: 'Total messages sent by grant management.',
  request_approval_rate: 'Percentage of requests that were approved or partially approved out of all requests (excluding STARTED).',
  request_abandon_rate: 'Percentage of requests that are STARTED out of all requests.',
}

export default {
  name: 'CanaryDashboard',
  components: {
    LoadSpinner,
  },
  mixins: [analyticsMixin],
  data() {
    return {
      filterableMetrics: FILTERABLE_METRICS,
      allTimeMetrics: ALL_TIME_METRICS,
      currentMetrics: [... FILTERABLE_METRICS, ALL_TIME_METRICS],
      friendlyMetricNames: FRIENDLY_METRIC_NAMES,
      metricDetails: METRIC_DETAILS,
      metrics: {},
      selectedDateRange: "all_time",
      isCustomDatePickerVisible: false,
      selectedStartDate: null, // forces md-datepicker to Date model
      selectedEndDate: null, // forces md-datepicker to Date model
      reportFilter: null,
      loading: true,
    }
  },
  created() {
    this.refresh()
  },
  computed: {
    enabledMetrics() {
      return this.filterableMetrics.concat(this.allTimeMetrics)
    },
  },
  methods: {
    async refresh(reportFilter = null) {
      this.loading = true
      let updatedMetrics = {}

      // Filterable metrics
      for (let i = 0; i < this.filterableMetrics.length; i++) {
        let metricName = this.filterableMetrics[i]
        var metricData = await CanaryDashboard.getMetric(metricName, reportFilter?.startDate, reportFilter?.endDate)
        updatedMetrics[metricName] = { name: metricName, data: metricData }
      }

      // All-time metrics
      for (let i = 0; i < this.allTimeMetrics.length; i++) {
        let metricName = this.allTimeMetrics[i]
        var metricData = await CanaryDashboard.getMetric(metricName, reportFilter?.startDate, reportFilter?.endDate)
        updatedMetrics[metricName] = { name: metricName, data: metricData }
      }

      this.metrics = updatedMetrics
      this.loading = false
    },
    dateRangeChanged(value) {
      if (value == this.selectedDateRange) { return; }

      this.selectedDateRange = value;

      switch (this.selectedDateRange) {
        case "all_time":
          this.isCustomDatePickerVisible = false;
          this.setReportFilter(null);
          break;

        case "year_to_date":
          this.isCustomDatePickerVisible = false;
          const now = new Date(Date.now());
          const prevYear = now.getFullYear() - 1;
          const yearAgo = new Date(new Date(Date.now()).setFullYear(prevYear));

          const filter = {
            startDate: yearAgo,
            endDate: now
          };

          this.setReportFilter(filter);
          break;

        case "custom":
          this.isCustomDatePickerVisible = true;
          this.buildCustomDateRangeFilter();
          break;
      }
    },
    buildCustomDateRangeFilter() {
      const start = this.selectedStartDate;
      const end = this.selectedEndDate;

      if (!start || !end) {
        return
      }

      const startTimestamp = Date.parse(start);
      const endTimestamp = Date.parse(end);
      if (isNaN(startTimestamp) || isNaN(endTimestamp)) {
        return;
      }

      const startDate = new Date(startTimestamp);
      const endDate = new Date(endTimestamp);

      this.setReportFilter({ startDate, endDate });
    },
    dateValueChanged(newValue, oldValue) {
      // Work around md-datepicker giving us a string instead of a Date
      const newTimestamp = newValue ? Date.parse(newValue) : null;
      const oldTimestamp = oldValue ? Date.parse(oldValue) : null;
      if (isNaN(newTimestamp) || isNaN(oldTimestamp)) { return }

      // Work around md-datepicker firing two identical events for a single
      // change event.
      // See https://github.com/vuematerial/vue-material/issues/2103
      if (oldTimestamp == newTimestamp) { return }

      const newDate = new Date(newTimestamp);
      const oldDate = new Date(oldTimestamp);

      // Work around md-datepicker triggering two watch events with different
      // timezone data for the same change event
      if (
        oldTimestamp && newTimestamp &&
        (
          (new Date(oldTimestamp)).toDateString() == (new Date(newTimestamp)).toDateString()
        )
      ) {
        return;
      }

      this.buildCustomDateRangeFilter();
    },
    setReportFilter(filter) {
      // Setting null value clears the filter
      if (!filter) {
        this.reportFilter = null;
        return;
      }

      // Ignore incomplete filter
      if (!filter.startDate || !filter.endDate) {
        return;
      }

      // Ignore invalid filter
      if (filter.startDate > filter.endDate) {
        // @TODO: set validation error in UI
        return;
      }

      // Ignore new filter of same value as existing
      if (
        this.reportFilter &&
        filter.startDate == this.reportFilter.startDate &&
        filter.endDate == this.reportFilter.endDate
      ) {
        return;
      }

      this.reportFilter = filter;
    },
  },
  watch: {
    selectedStartDate(newStart, oldStart) {
      this.dateValueChanged(newStart, oldStart);
    },
    selectedEndDate(newEnd, oldEnd) {
      this.dateValueChanged(newEnd, oldEnd);
    },
    reportFilter(newFilter) {
      this.refresh(this.reportFilter);
    },
  },
}
</script>

<style lang="scss" scoped>
.canary-dashboard {
  display: flex;
  flex-direction: column;

  .header-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin: 0 2rem;
  }

  .Report__Filter {
    display: flex;
    flex-direction: row;
    margin-bottom: 2rem;

    .md-field {
      margin: 0;
    }
  }

  .Report__Filter__DateRange {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .md-override.Report__Filter__DateRange-field {
    margin: 0 0 0 1rem;
    padding: 0;
  }

  .md-override.Report__Filter__DateRange-picker {
    margin: 0;
  }

  .canary-dashboard-heading {
    margin-left: 2rem;
  }

  .canary-dashboard-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;

    .metric-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin: 0 1.5rem 3rem 1.5rem;

      .metric-header {
        display: flex;
        flex-direction: row;
        margin-bottom: 1rem;
      }

      .metric-label {
        color: #7d7d7d;
        font-size: 0.9rem;
        font-weight: bold;
        text-transform: uppercase;
        text-decoration: underline;
        text-decoration-style: dotted;
      }

      .metric-tooltip-wrapper {
        cursor: pointer;
        margin-left: 0.5rem;
        height: 16px;
        width: 16px;

        .metric-tooltip {
          display: none;
          background: var(--dark-brand-color);
          color: white;
          font-size: 0.8rem;
          font-weight: bold;
          padding: 1rem 0.5rem;
          position: relative;
          width: 200px;
          text-align: center;
          left: 1.5rem;
          top: -4.5rem;
        }

        &:hover {
          .metric-tooltip {
            display: block;
          }
        }
      }

      .metric-data {
        background-color: #f9f9f9;
        display: flex;
        flex-direction: column;
        justify-content: center;
        font-size: 1.5rem;
        font-weight: bold;
        padding: 2.5rem 4.5rem;
        text-align: center;

        &.request_drop_off {
          font-size: 1rem;
        }

        ::v-deep .extras {
          margin-top: 0.5rem;
          font-size: 0.7rem;
        }
      }
    }
  }
}
</style>
